import React, { useState, useEffect } from 'react';
import {
  Modal, Card, Row, Col, Tooltip, Input, Space, Typography, Button, Empty, Spin
} from 'antd';
import {
  ThunderboltOutlined,
  WalletOutlined,
  HomeOutlined,
  CreditCardOutlined,
  SafetyOutlined,
  SearchOutlined,
  ArrowLeftOutlined,
  StarOutlined,
  RightOutlined
} from '@ant-design/icons';
import 'antd/dist/antd.css';
import CommonBillPayment from './commonBillPayment';
import MobileRecharge from './mobileRecharge';
import './UtilityPayment.css';
import CommingSoon from '../../container/commingsoon';
import { getNewRequest } from '../../Utils/AppApi';
import { BBPS_IMWALLET_CATEGORIES } from '../../Utils/appConstants';
import { useHistory } from "react-router-dom";

const { Text } = Typography;

// Brand colors
const PRIMARY_COLOR = '#C9262C';
const ACCENT_COLOR = '#FFDD09';

function UtilityPayment(props) {
  const history = useHistory();

  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [selectCategory, setSelectCategory] = useState(null);
  const [isBillPayment, setIsBillPayment] = useState(false); // flag to use CommonBillPayment
  const [searchTerm, setSearchTerm] = useState('');
  const [commingSoon, setCommingSoon] = useState(false);

  const handleOpenClose = () => setCommingSoon(false);

  // ---- Fetch categories dynamically from API ----
  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        const response = await getNewRequest(BBPS_IMWALLET_CATEGORIES, {}, history);
        if (response.success && Array.isArray(response.data)) {
          let mapped = response.data.map((item, index) => ({
            cat_id: item.Category || item.category_name || `cat_${index}`,
            category_name: item.Category || item.category_name || 'Unnamed',
          }));

          // ---- Merge Prepaid and DTH into one card ----
          const prepaid = mapped.find(c => c.category_name === 'Prepaid');
          const dth = mapped.find(c => c.category_name === 'DTH');
          if (prepaid && dth) {
            mapped = mapped.filter(c => c.category_name !== 'DTH');
            prepaid.category_name = 'Mobile / DTH Via API';
          }
          setCategories(mapped);
        } else {
          console.warn('Failed to fetch categories, using fallback static list');
          setCategories(STATIC_CATEGORIES_FALLBACK);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
        setCategories(STATIC_CATEGORIES_FALLBACK);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, [history]);

  // Static fallback list (if API fails)
  const STATIC_CATEGORIES_FALLBACK = [
    { cat_id: 1, category_name: "Landline" },
    { cat_id: 2, category_name: "Broadband" },
    { cat_id: 3, category_name: "Mobile / DTH Via API" },
    { cat_id: 4, category_name: "Cable" },
    { cat_id: 5, category_name: "Postpaid" },
    { cat_id: 6, category_name: "Datacard Prepaid" },
    { cat_id: 7, category_name: "Electricity" },
    { cat_id: 8, category_name: "Gas" },
    { cat_id: 9, category_name: "Water" },
    { cat_id: 10, category_name: "Insurance" },
    { cat_id: 11, category_name: "EMI Payments" },
    { cat_id: 12, category_name: "Traffic Challan" },
    { cat_id: 13, category_name: "LPG Booking" },
    { cat_id: 14, category_name: "Fastag" },
    { cat_id: 15, category_name: "Hospital" },
    { cat_id: 16, category_name: "Digital Voucher" },
    { cat_id: 17, category_name: "Municipality Tax" },
    { cat_id: 18, category_name: "DTH" }
  ];

  // ---- handleService with isBillPayment flag ----
  const handleService = (categoryName, cat_id) => {
    // Quick Payment (Recharge) – "Mobile / DTH Via API"
    if (categoryName === 'Mobile / DTH Via API' || categoryName === 'Prepaid' || categoryName === 'DTH') {
      setSelectCategory('Prepaid');
      setIsBillPayment(false); // triggers MobileRecharge
      return;
    }

    // Home Utilities (Bill Payment) – static entries
    if (categoryName === 'Mobile Prepaid') {
      setSelectCategory('Prepaid');       // backend expects "Prepaid"
      setIsBillPayment(true);            // opens CommonBillPayment
      return;
    }
    if (categoryName === 'DTH Recharge') {
      setSelectCategory('DTH');           // backend expects "DTH"
      setIsBillPayment(true);            // opens CommonBillPayment
      return;
    }

    // All other categories – bill payment
    setSelectCategory(categoryName);
    setIsBillPayment(true);
  };

  const getCategoryIcon = (categoryName) => {
    const name = categoryName.toUpperCase();
    if (name.includes('ELECTRICITY')) return <ThunderboltOutlined style={{ fontSize: '28px' }} />;
    if (name.includes('WATER')) return <i className="fas fa-tint" style={{ fontSize: '28px' }} />;
    if (name.includes('GAS') || name.includes('LPG')) return <i className="fas fa-fire" style={{ fontSize: '28px' }} />;
    if (name.includes('BROADBAND')) return <i className="fas fa-wifi" style={{ fontSize: '28px' }} />;
    if (name.includes('MOBILE') || name.includes('PREPAID') || name.includes('POSTPAID')) return <i className="fas fa-mobile-alt" style={{ fontSize: '28px' }} />;
    if (name.includes('FASTAG')) return <i className="fas fa-tag" style={{ fontSize: '28px' }} />;
    if (name.includes('INSURANCE')) return <SafetyOutlined style={{ fontSize: '28px' }} />;
    if (name.includes('CREDIT') || name.includes('EMI') || name.includes('LOAN')) return <CreditCardOutlined style={{ fontSize: '28px' }} />;
    if (name.includes('DTH')) return <i className="fas fa-tv" style={{ fontSize: '28px' }} />;
    if (name.includes('LANDLINE')) return <i className="fas fa-phone" style={{ fontSize: '28px' }} />;
    if (name.includes('CABLE')) return <i className="fas fa-plug" style={{ fontSize: '28px' }} />;
    if (name.includes('TRAFFIC')) return <i className="fas fa-car" style={{ fontSize: '28px' }} />;
    if (name.includes('HOSPITAL')) return <i className="fas fa-hospital" style={{ fontSize: '28px' }} />;
    if (name.includes('VOUCHER')) return <i className="fas fa-gift" style={{ fontSize: '28px' }} />;
    if (name.includes('MUNICIPALITY')) return <i className="fas fa-building" style={{ fontSize: '28px' }} />;
    if (name.includes('AGENT')) return <i className="fas fa-user-tie" style={{ fontSize: '28px' }} />;
    return <HomeOutlined style={{ fontSize: '28px' }} />;
  };

  const getCategoryColor = (categoryName) => {
    const name = categoryName.toUpperCase();
    if (name.includes('ELECTRICITY')) return '#ff6b6b';
    if (name.includes('WATER')) return '#4dabf7';
    if (name.includes('GAS') || name.includes('LPG')) return '#ff922b';
    if (name.includes('BROADBAND')) return '#9775fa';
    if (name.includes('PREPAID') || name.includes('POSTPAID') || name.includes('MOBILE')) return '#51cf66';
    if (name.includes('FASTAG')) return '#fcc419';
    if (name.includes('INSURANCE')) return '#339af0';
    if (name.includes('EMI') || name.includes('CREDIT') || name.includes('LOAN')) return '#f06595';
    if (name.includes('DTH')) return '#be4bdb';
    if (name.includes('LANDLINE')) return '#20c997';
    if (name.includes('CABLE')) return '#fd7e14';
    if (name.includes('TRAFFIC')) return '#e03131';
    if (name.includes('HOSPITAL')) return '#12b886';
    if (name.includes('VOUCHER')) return '#f59f00';
    if (name.includes('MUNICIPALITY')) return '#5f3dc4';
    if (name.includes('AGENT')) return '#845ef7';
    return '#868e96';
  };

  // Filter categories based on search
  const filteredCategories = categories.filter(category =>
    category.category_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ---- getGroupedCategories ----
  const getGroupedCategories = () => {
    const grouped = {
      'Quick Payment': [],
      'Home Utilities': [],
      'Financial & Legal': [],
      'Other Services': []
    };

    filteredCategories.forEach(category => {
      const name = category.category_name;
      const nameUpper = name.toUpperCase();

      // Special case: Prepaid Meter (must go to Home Utilities)
      if (name === 'Prepaid Meter' || nameUpper === 'PREPAID METER') {
        grouped['Home Utilities'].push(category);
        return;
      }

      // Generic keyword matching
      const groupRules = [
        {
          group: 'Quick Payment',
          keywords: ['MOBILE', 'PREPAID', 'DTH', 'FASTAG', 'GAS', 'ELECTRICITY', 'LOAN REPAYMENT', 'LOAN']
        },
        {
          group: 'Home Utilities',
          keywords: ['LANDLINE', 'MOBILE POSTPAID', 'POSTPAID', 'BROADBAND', 'CABLE', 'WATER']
        },
        {
          group: 'Financial & Legal',
          keywords: ['INSURANCE', 'AGENT COLLECTION', 'AGENT']
        }
      ];

      let assigned = false;
      for (const rule of groupRules) {
        if (rule.keywords.some(keyword => nameUpper.includes(keyword))) {
          grouped[rule.group].push(category);
          assigned = true;
          break;
        }
      }

      if (!assigned) {
        grouped['Other Services'].push(category);
      }
    });

    // ---- ADD STATIC ENTRIES to Home Utilities ----
    const staticMobilePrepaid = { cat_id: 'mobile_prepaid', category_name: 'Mobile Prepaid' };
    const staticDthRecharge = { cat_id: 'dth_recharge', category_name: 'DTH Recharge' };

    const hasMobilePrepaid = grouped['Home Utilities'].some(c => c.category_name === 'Mobile Prepaid');
    const hasDthRecharge = grouped['Home Utilities'].some(c => c.category_name === 'DTH Recharge');
    if (!hasMobilePrepaid) grouped['Home Utilities'].push(staticMobilePrepaid);
    if (!hasDthRecharge) grouped['Home Utilities'].push(staticDthRecharge);

    return Object.entries(grouped).filter(([_, services]) => services.length > 0);
  };

  const categoryGroups = getGroupedCategories();

  // CSS (unchanged)
  const removeMarginsStyles = `
    /* Remove margins from parent containers */
    .bbps-modern-container {
      margin: 0 !important;
      padding: 0 !important;
    }
    .content-wrapper {
      padding: 0 !important;
      margin: 0 !important;
    }
    .content {
      padding: 0 !important;
      margin: 0 !important;
    }
    .container-fluid {
      padding-left: 0 !important;
      padding-right: 0 !important;
      margin: 0 !important;
    }
    .row {
      margin-left: 0 !important;
      margin-right: 0 !important;
    }
    .col-24, .col-span-24 {
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
    .ant-card {
      margin: 0 !important;
    }
    .ant-card-body {
      padding: 20px !important;
    }
    .bbps-modern-card {
      margin: 0 !important;
      border-radius: 0 !important;
    }
    .bbps-modern-search {
      margin-top: 0 !important;
    }
    .pt-4 {
      padding-top: 0 !important;
    }
    .pt-2 {
      padding-top: 0 !important;
    }
    .pb-5 {
      padding-bottom: 0 !important;
    }
  `;

  if (loadingCategories) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <Spin size="large" tip="Loading categories..." />
      </div>
    );
  }

  return (
    <div className="bbps-modern-container" style={{ margin: 0, padding: 0 }}>
      <style>{removeMarginsStyles}</style>
      <style>{`
        .bbps-modern-search .bbps-search-input-modern:focus {
          border-color: ${PRIMARY_COLOR} !important;
          box-shadow: 0 0 0 2px ${PRIMARY_COLOR}20 !important;
        }
        .bbps-modern-service-card {
          transition: all 0.3s;
        }
        .bbps-modern-service-card:hover {
          border-color: ${PRIMARY_COLOR} !important;
          transform: translateY(-4px);
          box-shadow: 0 8px 20px ${PRIMARY_COLOR}20;
        }
        .bbps-modern-service-card .bbps-modern-service-tag {
          background: ${PRIMARY_COLOR} !important;
        }
        .bbps-modern-back-btn {
          background: ${PRIMARY_COLOR} !important;
          border-color: ${PRIMARY_COLOR} !important;
        }
        .bbps-modern-back-btn:hover {
          background: ${PRIMARY_COLOR}dd !important;
        }
        .ant-btn-primary {
          background: ${PRIMARY_COLOR} !important;
          border-color: ${PRIMARY_COLOR} !important;
        }
        .ant-btn-primary:hover {
          background: ${PRIMARY_COLOR}dd !important;
          border-color: ${PRIMARY_COLOR}dd !important;
        }
        .bbps-group-icon-wrapper {
          color: ${PRIMARY_COLOR} !important;
        }
        .ant-steps-item-process .ant-steps-item-icon {
          background: ${PRIMARY_COLOR} !important;
          border-color: ${PRIMARY_COLOR} !important;
        }
        .ant-steps-item-finish .ant-steps-item-icon {
          border-color: ${PRIMARY_COLOR} !important;
        }
        .ant-steps-item-finish .ant-steps-item-icon .ant-steps-icon {
          color: ${PRIMARY_COLOR} !important;
        }
        .ant-steps-item-finish > .ant-steps-item-container > .ant-steps-item-tail::after {
          background: ${PRIMARY_COLOR} !important;
        }
      `}</style>
      <div className="bbps-animated-bg">
        <div className="bbps-bg-shape bbps-shape-1"></div>
        <div className="bbps-bg-shape bbps-shape-2"></div>
        <div className="bbps-bg-shape bbps-shape-3"></div>
      </div>

      <div className="content-wrapper pt-4" style={{ padding: 0, margin: 0 }}>
        <div className="content" style={{ padding: 0, margin: 0 }}>
          <div className="container-fluid" style={{ padding: 0, margin: 0 }}>
            <Row style={{ margin: 0 }}>
              <Col span={24} style={{ padding: 0 }}>
                <Card className="bbps-modern-card" bordered={false} style={{ margin: 0, borderRadius: 0 }}>
                  {!selectCategory ? (
                    <>
                      {/* <div className="bbps-modern-search mb-5">
                        <div className="bbps-search-wrapper">
                          <SearchOutlined className="bbps-search-icon-modern" />
                          <input
                            type="text"
                            className="bbps-search-input-modern"
                            placeholder="Search for electricity, broadband, credit card, fastag or any bill..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          {searchTerm && (
                            <button
                              className="bbps-search-clear"
                              onClick={() => setSearchTerm('')}
                            >
                              ×
                            </button>
                          )}
                        </div>
                        {searchTerm && filteredCategories.length > 0 && (
                          <div className="bbps-search-results">
                            Found {filteredCategories.length} service(s)
                          </div>
                        )}
                      </div> */}

                      {filteredCategories.length > 0 ? (
                        categoryGroups.map(([groupName, services]) => (
                          <div key={groupName} className="bbps-modern-group mb-5">
                            <div className="bbps-modern-group-header">
                              <div className="bbps-group-icon-wrapper">
                                {groupName === 'Quick Payment' && <ThunderboltOutlined style={{ color: ACCENT_COLOR }} />}
                                {groupName === 'Home Utilities' && <HomeOutlined style={{ color: ACCENT_COLOR }} />}
                                {groupName === 'Financial & Legal' && <WalletOutlined style={{ color: ACCENT_COLOR }} />}
                                {groupName === 'Other Services' && <StarOutlined style={{ color: ACCENT_COLOR }} />}
                              </div>
                              <h3 className="bbps-modern-group-title">{groupName}</h3>
                              <div className="bbps-group-divider"></div>
                              <span className="bbps-group-count">{services.length} services</span>
                            </div>

                            <Row gutter={[20, 20]} style={{ margin: 0 }}>
                              {services.map((category) => (
                                <Col xs={12} sm={8} md={6} lg={4} xl={3} key={category.cat_id} style={{ padding: 0 }}>
                                  <Tooltip title={`Click to pay ${category.category_name} bill`} placement="bottom">
                                    <div
                                      className="bbps-modern-service-card"
                                      onClick={() => handleService(category.category_name, category.cat_id)}
                                      style={{ '--card-color': getCategoryColor(category.category_name), margin: '8px' }}
                                    >
                                      <div className="bbps-card-glow"></div>
                                      <div className="bbps-modern-service-icon" style={{ background: `${getCategoryColor(category.category_name)}15` }}>
                                        {category.icon ? (
                                          <img
                                            src={category.icon}
                                            alt={category.category_name}
                                            className="bbps-service-icon-img-modern"
                                          />
                                        ) : (
                                          <div className="bbps-icon-wrapper" style={{ color: getCategoryColor(category.category_name) }}>
                                            {getCategoryIcon(category.category_name)}
                                          </div>
                                        )}
                                      </div>
                                      <h5 className="bbps-modern-service-name">
                                        {category.category_name}
                                      </h5>
                                      <div className="bbps-modern-service-tag">
                                        Pay Now <RightOutlined style={{ color: ACCENT_COLOR, fontStyle: 'bold' }} />
                                      </div>
                                      <div className="bbps-card-hover-effect"></div>
                                    </div>
                                  </Tooltip>
                                </Col>
                              ))}
                            </Row>
                          </div>
                        ))
                      ) : (
                        <Empty
                          description="No services found matching your search"
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        >
                          <Button onClick={() => setSearchTerm('')} type="primary">
                            Clear Search
                          </Button>
                        </Empty>
                      )}
                    </>
                  ) : (
                    <div className="bbps-modern-payment-section" style={{ padding: 0 }}>
                      <Button
                        className="bbps-modern-back-btn"
                        onClick={() => {
                          setSelectCategory(null);
                          setIsBillPayment(false);
                        }}
                        icon={<ArrowLeftOutlined />}
                        style={{ marginBottom: 0, background: PRIMARY_COLOR, borderColor: PRIMARY_COLOR }}
                      >
                        BBPS HOME PAGE
                      </Button>

                      {/* ---- Routing logic using isBillPayment flag ---- */}
                      {!isBillPayment && selectCategory === 'Prepaid' ? (
                        // Quick Payment → MobileRecharge
                        <MobileRecharge />
                      ) : (
                        // All other cases → CommonBillPayment
                        // This includes "Mobile Prepaid" (maps to "Prepaid" with isBillPayment=true)
                        // and "DTH Recharge" (maps to "DTH" with isBillPayment=true)
                        <CommonBillPayment category={selectCategory} />
                      )}

                      <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
                        <Button
                          className="bbps-modern-back-btn"
                          onClick={() => {
                            setSelectCategory(null);
                            setIsBillPayment(false);
                          }}
                          icon={<ArrowLeftOutlined />}
                          style={{
                            marginBottom: 0,
                            background: PRIMARY_COLOR,
                            borderColor: PRIMARY_COLOR,
                          }}
                        >
                          BBPS HOME PAGE
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              </Col>
            </Row>
          </div>
        </div>
      </div>

      {/* Coming Soon Modal */}
      <Modal
        open={commingSoon}
        onCancel={handleOpenClose}
        footer={false}
        width={500}
        centered
      >
        <CommingSoon />
      </Modal>
    </div>
  );
}

export default UtilityPayment;